import type { ComponentProps, PropsWithChildren } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type {
  BorderRadiusProps,
  BorderTopProps,
  BoxShadowProps,
  ColorProps,
  DisplayProps,
  LeftProps,
  MaxWidthProps,
  RightProps,
  WidthProps,
  ZIndexProps,
} from 'styled-system';
import {
  borderRadius as borderRadiusUtil,
  borderTop,
  boxShadow,
  color,
  display as displayUtil,
  maxWidth as maxWidthUtil,
  space,
  width,
} from 'styled-system';

import type { SpaceProps } from 'common/theme/system-types';
import type { BoxSizingProps } from 'common/theme/system-utilities';
import { boxSizing } from 'common/theme/system-utilities';
import type { Viewport } from '../enums/Viewport';
import { StickyBottomDependent } from './StickyDependent';

export interface StickyContainerProps
  extends
    PropsWithChildren,
    ZIndexProps,
    SpaceProps,
    RightProps,
    LeftProps,
    DisplayProps,
    MaxWidthProps,
    BorderRadiusProps {
  viewport: Viewport;
  dependsOn: string;
  id?: string;
  additionalElementOffset?: number;
}

const StickyDependentStyles = styled.div<
  WidthProps &
    ColorProps &
    SpaceProps &
    DisplayProps &
    BorderTopProps &
    MaxWidthProps &
    BorderRadiusProps &
    BoxSizingProps &
    BoxShadowProps
>`
  ${width};
  ${color};
  ${space};
  ${displayUtil};
  ${borderTop};
  ${maxWidthUtil};
  ${borderRadiusUtil};
  ${boxSizing};
  ${boxShadow};
`;

const stickyDependentStyles: ComponentProps<typeof StickyDependentStyles> = {
  display: ['block', null, 'none'],
  backgroundColor: 'sys.neutral.background.default',
  width: '100%',
  boxSizing: 'border-box',
  boxShadow: '0 -2px 5px rgba(0, 0, 0, 0.1)',
};

const StickyContainer = ({
  id,
  viewport,
  dependsOn,
  children,
  additionalElementOffset = 8,
  ...customStyles
}: StickyContainerProps) => {
  const { py, display, maxWidth, borderRadius } = customStyles;

  return (
    <StickyBottomDependent
      viewport={viewport}
      dependsOn={dependsOn}
      additionalElementOffset={additionalElementOffset}
      right={customStyles?.right}
      left={customStyles?.left}
      zIndex={customStyles?.zIndex}
    >
      <StickyDependentStyles
        id={id}
        data-testid="sticky-container"
        {...stickyDependentStyles}
        {...(py ? { py } : {})}
        {...(display ? { display } : {})}
        {...(maxWidth ? { maxWidth } : {})}
        {...(borderRadius ? { borderRadius } : {})}
      >
        {children}
      </StickyDependentStyles>
    </StickyBottomDependent>
  );
};

export default StickyContainer;
